import {
  EditOutlined,
  FileTextOutlined,
  HomeOutlined,
  PhoneOutlined,
  PrinterOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { css } from '@emotion/css';
import { Button, Flex, Modal, Popconfirm, Popover, Select, Tag, Tooltip, theme } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { PatientDataInput } from '../../graphql/generated/graphql.ts';
import { BillingType, Gender } from '../../graphql/generated/graphql.ts';
import { useAppStore } from '../../hooks/store/useAppStore.ts';
import { usePrintEmptyLabelOf } from '../../hooks/usePrintEmptyLabel';
import { PrintButton } from '../../PrintButton.tsx';
import { personAge, SHORT_DATE_FORMAT } from '../../utils/dateFormatUtils';
import { hasCompleteAddress, patientAddress, patientFullName } from '../../utils/patient';
import { Forms } from '../forms/Forms.tsx';

const { useToken } = theme;

interface PatientHeaderProps {
  patientData: PatientDataInput;
  insuranceName: string;
  patientBilling: boolean;
  availableBillingTypes: BillingType[];
  showDefaultBillingChooser: boolean;
  onEdit?: () => void;
  menu?: ReactNode;
}

export const PatientHeader: React.FC<PatientHeaderProps> = ({
  patientData,
  insuranceName,
  patientBilling,
  availableBillingTypes,
  showDefaultBillingChooser,
  onEdit,
  menu,
}) => {
  const { token } = useToken();
  const address = patientAddress(patientData);
  const addressComplete = hasCompleteAddress(patientData);
  const printEmptyLabelOf = usePrintEmptyLabelOf();

  const [formsOpen, setFormsOpen] = useState(false);

  const { preferredBillingType, setPreferredBillingType } = useAppStore(
    useShallow(state => ({
      preferredBillingType: state.preferredBillingType,
      setPreferredBillingType: state.setPreferredBillingType,
    }))
  );

  useEffect(() => {
    if (!preferredBillingType) {
      // auto
      return;
    }
    if (!availableBillingTypes.some(it => it === preferredBillingType)) {
      setPreferredBillingType(null);
    }
  }, [preferredBillingType, setPreferredBillingType, availableBillingTypes]);

  return (
    <PageHeader
      title={patientFullName(patientData)}
      tags={
        <Flex gap="small">
          {onEdit && (
            <Popconfirm
              key="edit"
              title={
                <div style={{ maxWidth: '300px' }}>
                  Wenn Sie die Stammdaten des Patienten bearbeiten, dann werden sich dadurch möglicherweise die anforderbaren Parameter und
                  deren Verrechnung ändern.
                </div>
              }
              onConfirm={onEdit}
              okText="Patient bearbeiten"
              cancelText="Abbrechen"
            >
              <Button key="edit" icon={<EditOutlined />} type="text" />
            </Popconfirm>
          )}
          <Button key="forms" icon={<FileTextOutlined />} type="text" onClick={() => setFormsOpen(true)} />
          <PrintButton key="empty-label" icon={<PrinterOutlined />} type="text" onClick={() => printEmptyLabelOf(patientData)} />
        </Flex>
      }
      className={css`
        padding: ${token.paddingMD}px 0;
        .ant-page-header-heading {
          column-gap: ${token.marginMD}px;
        }
      `}
      avatar={{
        style: { flexShrink: 0 },
        children: patientData.lastName.charAt(0).toUpperCase() + patientData.firstName.charAt(0).toUpperCase(),
      }}
      extra={
        showDefaultBillingChooser ? (
          <Flex align="center" gap={token.paddingXS}>
            <span>Verrechnung</span>
            <Select
              options={[
                {
                  label: 'Automatisch',
                  value: '',
                },
                {
                  label: 'Privat an Zuweiser',
                  value: BillingType.DOCTOR,
                  disabled: !availableBillingTypes.some(it => it === BillingType.DOCTOR),
                },
                {
                  label: 'Privat an Patient',
                  value: BillingType.PATIENT,
                  disabled: !availableBillingTypes.some(it => it === BillingType.PATIENT),
                },
                {
                  label: 'Kasse (FA)',
                  value: BillingType.INSURANCE,
                  disabled: !availableBillingTypes.some(it => it === BillingType.INSURANCE),
                },
              ]}
              value={preferredBillingType ?? ''}
              onChange={billingType => setPreferredBillingType(billingType ? (billingType as BillingType) : null)}
              style={{ width: '170px' }}
            />
          </Flex>
        ) : undefined
      }
    >
      <Flex
        gap="small"
        wrap
        className={css`
          padding-bottom: ${token.paddingSM}px;
        `}
      >
        <Tag>
          {patientData.gender === Gender.FEMALE ? 'W' : 'M'} {personAge(patientData.birthday).toLocaleString('de-AT')} Jahre
        </Tag>
        <Tag>
          {patientData.svnr ? (
            <span>
              {patientData.svnr} ({dayjs(patientData.birthday).format(SHORT_DATE_FORMAT)})
            </span>
          ) : (
            <span>{dayjs(patientData.birthday).format(SHORT_DATE_FORMAT)}</span>
          )}
        </Tag>
        <Tag>{insuranceName}</Tag>
        <Popover
          title="Mitversichert bei"
          classNames={{
            root: css`
              max-width: 500px;
            `,
          }}
          content={
            <Flex wrap gap="small">
              <Tag icon={<UserOutlined />}>{patientData.insuredPerson && patientFullName(patientData.insuredPerson)}</Tag>
              <Tag>
                {patientData.insuredPerson?.gender ? (patientData.insuredPerson.gender === Gender.FEMALE ? 'W ' : 'M ') : ''}
                {patientData.insuredPerson?.birthday && personAge(patientData.insuredPerson?.birthday).toLocaleString('de-AT')} Jahre
              </Tag>
              <Tag>
                {patientData.insuredPerson?.svnr} ({dayjs(patientData.insuredPerson?.birthday).format(SHORT_DATE_FORMAT)})
              </Tag>
              <Tag icon={<PhoneOutlined />}>{patientData.insuredPerson?.phone}</Tag>
              <Tag icon={<HomeOutlined />}>{patientAddress(patientData.insuredPerson)}</Tag>
            </Flex>
          }
        >
          <Tag hidden={!patientData.insuredPerson} icon={<TeamOutlined />}>
            Mitversichert
          </Tag>
        </Popover>
        <Tag hidden={!patientData.phone.length} icon={<PhoneOutlined />}>
          {patientData.phone}
        </Tag>
        <Tag hidden={(patientBilling && !addressComplete) || !address.length} icon={<HomeOutlined />}>
          {address}
        </Tag>
        <Tooltip
          title={
            <>
              Die Adresse ist unvollständig, daher ist <b>Privat an Patient</b> nicht möglich
            </>
          }
        >
          <Tag hidden={!patientBilling || addressComplete} color="orange" icon={<HomeOutlined />}>
            Privat an Patient <i>nicht möglich</i>
          </Tag>
        </Tooltip>
      </Flex>
      {menu}
      <Modal
        title={`Formulare für ${patientFullName(patientData)}`}
        width={768}
        open={formsOpen}
        footer={null}
        onCancel={() => setFormsOpen(false)}
        destroyOnHidden
      >
        <Forms patientData={patientData} />
      </Modal>
    </PageHeader>
  );
};
